import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../../context/UserContext';
import { useLanguage } from '../../../context/LanguageContext';
import { useCustomerAuthTranslation } from './translations';
import LanguageSwitcher from '../../../components/layout/navbar/LanguageSwitcher/LanguageSwitcher';
import './AuthPage.css';
// import { CheckCircle } from 'lucide-react'; // unused
import { useDynamicPageTitle } from '../../../hooks/useDynamicPageTitle';

const AuthPage = ({ panelMode = false, onClose, initialEmail = '' }) => {
  const { language } = useLanguage();
  const { t } = useCustomerAuthTranslation(language);

  // Set dynamic page title (only if not in panel mode)
  useDynamicPageTitle(panelMode ? null : 'auth');

  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [useEmailPassword, setUseEmailPassword] = useState(true); // Default to email/password
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  const { currentUser, sendAuthLinkToEmail, completeEmailLinkSignIn, checkForEmailLink, sendPasswordReset } = useUser();

  const navigate = useNavigate();
  const location = useLocation();

  // Get redirect path from query params or state
  const getRedirectPath = useCallback(() => {
    const queryParams = new URLSearchParams(location.search);
    return location.state?.from || queryParams.get('redirect') || '/';
  }, [location]);

  // Check if user is already logged in
  useEffect(() => {
    if (currentUser && !loading) {
      const destination = getRedirectPath();
      navigate(destination, { replace: true });
    }
  }, [currentUser, navigate, loading, getRedirectPath]);

  // SIMPLIFIED - Check if this page was opened from an email link
  useEffect(() => {
    const handleEmailLink = async () => {
      // Only check for email links if the URL actually contains email link parameters
      const currentUrl = window.location.href;
      if (!currentUrl.includes('oobCode') || !currentUrl.includes('mode=signIn')) {
        return;
      }

      if (checkForEmailLink()) {
        try {
          setLoading(true);
          const result = await completeEmailLinkSignIn();
          if (result.success) {
            // Clear the URL params
            window.history.replaceState({}, document.title, window.location.pathname);

            // Get the redirect destination
            const destination = getRedirectPath();
            navigate(destination, { replace: true });
          } else {
            throw new Error(result.error || 'Email link sign-in failed');
          }
        } catch (err) {
          console.error('Email link sign-in error:', err);
          setError(err.message || 'Failed to sign in with email link. Please try again.');
        } finally {
          setLoading(false);
        }
      }
    };

    handleEmailLink();
  }, [checkForEmailLink, completeEmailLinkSignIn, navigate, getRedirectPath]);

  // Update email when initialEmail prop changes
  useEffect(() => {
    if (initialEmail && initialEmail.trim()) {
      setEmail(initialEmail.trim());
    }
  }, [initialEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (useEmailPassword && !password.trim()) {
      setError('Please enter your password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const userData = {
        password: useEmailPassword ? password : undefined,
        isRegistering: isRegistering // Pass registration flag
      };

      const result = await sendAuthLinkToEmail(
        email.trim(),
        isRegistering ? userData : (useEmailPassword ? { password } : {}),
      ); if (result.success) {
        if (result.emailSent) {
          setEmailSent(true);
        } else {
          // Direct sign-in succeeded, user will be redirected by useEffect
          // console.log('Authentication successful:', result.method);
        }
      } else {
        throw new Error(result.error || 'Authentication failed');
      }
    } catch (err) {
      console.error('Auth error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMode = () => {
    setIsRegistering(!isRegistering);
    setError('');
    setPassword(''); // Clear password when switching modes
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address first');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await sendPasswordReset(email.trim());
      setResetEmailSent(true);
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err.message || 'Failed to send password reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  if (panelMode) {
    // ✅ Compact inline panel (for dropdown menu)
    return (
      <div className="auth-panel">
        {resetEmailSent ? (
          <>
            <h4>{t.passwordResetSent}</h4>
            <p>{t.resetEmailMessage}</p>
            <p className="email-address">{email}</p>
            <p className="email-from">{t.resetEmailNote}</p>
            <button
              className="resend-link"
              onClick={() => {
                setResetEmailSent(false);
                setShowForgotPassword(false);
              }}
            >
              {t.backToSignIn}
            </button>
          </>
        ) : showForgotPassword ? (
          <>
            <h4 className="auth-header">{t.resetPassword}</h4>
            <div className="auth-form">
              <input
                type="email"
                placeholder={t.emailAddress}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="form-inputA"
              />
              <button
                type="button"
                className="submit-button"
                onClick={handleForgotPassword}
                disabled={loading}
              >
                {loading ? '...' : t.sendResetEmail}
              </button>
              <button
                type="button"
                className="toggle-mode-btn"
                onClick={() => setShowForgotPassword(false)}
              >
                {t.backToSignIn}
              </button>
            </div>
            {error && <p className="error-text">{error}</p>}
          </>
        ) : emailSent ? (
          <>
            <h4>{t.checkYourEmail}</h4>
            <p>{t.weveSentLink}</p>
            <p className="email-address">{email}</p>
            <p className="spam-note">{t.checkSpam}</p>
            <p className="email-from">{t.emailFrom}</p>
            <button className="resend-link" onClick={() => setEmailSent(false)}>
              {t.resendLink}
            </button>
          </>
        ) : (
          <>
            <h4 className="auth-header">{isRegistering ? t.register : t.signIn}</h4>
            <div className="auth-form">
              <input
                type="email"
                placeholder={t.emailAddress}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="form-inputA"
              />

              {useEmailPassword && (
                <input
                  type="password"
                  placeholder={t.password}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required={!emailSent}
                  className="form-inputA"
                />
              )}

              <button
                type="button"
                className="submit-button"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? '...' :
                  useEmailPassword ?
                    (isRegistering ? t.createAccount : t.signInWithPassword) :
                    t.sendSignInLink
                }
              </button>

              {useEmailPassword && !isRegistering && (
                <button
                  type="button"
                  className="forgot-password-link"
                  onClick={() => setShowForgotPassword(true)}
                >
                  {t.forgotPassword}
                </button>
              )}

              <button
                type="button"
                className="toggle-method-btn"
                onClick={() => setUseEmailPassword(!useEmailPassword)}
              >
                {useEmailPassword ? t.useEmailLink : t.usePassword}
              </button>

              <button
                type="button"
                className="toggle-mode-btn"
                onClick={handleToggleMode}
              >
                {isRegistering ? t.alreadyHaveAccount : t.needAccount}
              </button>
            </div>
            {error && <p className="error-text">{error}</p>}
          </>
        )}
      </div>
    );
  }

  // 🚫 If it's an email sign-in link, render nothing (no white page)
  const currentUrl = window.location.href;
  if (currentUrl.includes("oobCode") && currentUrl.includes("mode=signIn")) {
    return null;
  }

  // ✅ Default: full overlay modal
  return (
    <div className="auth-overlay" onClick={onClose}>
      {/* Language Switcher */}
      <div className="auth-language-switcher">
        <LanguageSwitcher />
      </div>

      <div className="auth-popup" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="close-button" onClick={onClose}>×</button>
        {resetEmailSent ? (
          <div className="email-sentA">
            <div className="success-badge" aria-hidden="true">
              <svg viewBox="0 0 24 24" className="success-check" focusable="false" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.2 12.6l1.8 1.8 4.2-4.8" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            <h2 className="auth-header">{t.passwordResetSent}</h2>

            <p className="email-message">{t.resetEmailMessage}</p>

            <p className="email-strong">{email}</p>
            <hr className="divider" />

            <p className="note-message">{t.resetEmailNote}</p>

            <button
              className="resend-link primary"
              onClick={() => {
                setResetEmailSent(false);
                setShowForgotPassword(false);
              }}
              aria-label="Back to sign in"
            >
              {t.backToSignIn}
            </button>
          </div>
        ) : showForgotPassword ? (
          <>
            <h2 className="auth-header">{t.resetPassword}</h2>
            <form className="auth-form" onSubmit={handleForgotPassword} noValidate>
              <label htmlFor="reset-email">{t.emailAddress}</label>
              <input
                type="email"
                id="reset-email"
                className="form-inputA"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />

              <button type="submit" className="submit-button" disabled={loading}>
                {loading ? '...' : t.sendResetEmail}
              </button>

              <button
                type="button"
                className="toggle-mode-btn"
                onClick={() => setShowForgotPassword(false)}
              >
                {t.backToSignIn}
              </button>
            </form>
            {error && <p className="error-text">{error}</p>}
          </>
        ) : emailSent ? (
          <div className="email-sentA">
            <div className="success-badge" aria-hidden="true">
              <svg viewBox="0 0 24 24" className="success-check" focusable="false" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.2 12.6l1.8 1.8 4.2-4.8" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            <h2 className="auth-header">{t.checkYourEmail}</h2>

            <p className="email-message">{t.weveSentLink}</p>

            <p className="email-strong">{email}</p>
            <hr className="divider" />

            <p className="note-message">{t.noteUseDevice}</p>

            <p className="spam-note">{t.checkSpam}</p>

            <p className="email-from-note">{t.emailFrom}</p>

            <button
              className="resend-link primary"
              onClick={() => setEmailSent(false)}
              aria-label="Resend sign-in link"
            >
              {t.resendLink}
            </button>
          </div>
        ) : (
          <>
            <h2 className="auth-header">{isRegistering ? t.register : t.signIn}</h2>
            <form className="auth-form" onSubmit={handleSubmit} noValidate>
              <label htmlFor="email">{t.emailAddress}</label>
              <input
                type="email"
                id="email"
                className="form-inputA"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />

              {useEmailPassword && (
                <>
                  <label htmlFor="password">{t.password}</label>
                  <input
                    type="password"
                    id="password"
                    className="form-inputA"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </>
              )}

              <button type="submit" className="submit-button" disabled={loading}>
                {loading ? '...' :
                  useEmailPassword ?
                    (isRegistering ? t.createAccount : t.signInWithPassword) :
                    t.sendSignInLink
                }
              </button>

              {useEmailPassword && !isRegistering && (
                <button
                  type="button"
                  className="forgot-password-link"
                  onClick={() => setShowForgotPassword(true)}
                >
                  {t.forgotPassword}
                </button>
              )}

              <button
                type="button"
                className="toggle-method-btn"
                onClick={() => setUseEmailPassword(!useEmailPassword)}
              >
                {useEmailPassword ? t.useEmailLink : t.usePassword}
              </button>

              <button
                type="button"
                className="toggle-mode-btn"
                onClick={handleToggleMode}
              >
                {isRegistering ? t.alreadyHaveAccount : t.needAccount}
              </button>
            </form>
            {error && <p className="error-text">{error}</p>}
          </>
        )}
      </div>
    </div>
  );



};
export default AuthPage;
